/**
 * DecorationModule — renders interior decorations as instanced models (Graphics G1).
 *
 * Each `structure_item` is placed by its LOCAL-to-structure transform (lx/ly/lz +
 * pitch/yaw/roll), composed with the owning structure's grounded world transform —
 * so a decoration sits correctly oriented on the structure's floor (ly is relative
 * to the floor at the structure base). The model resolves through the catalog
 * (decor:<prefab> → render_asset), the same swappable path as structures.
 *
 * Rendering is INSTANCED: WebGPURenderer allocates one UBO per render object, rebound
 * every frame, so per-row Object3Ds are the CPU bottleneck at scale (not draw calls
 * alone). ONE `StaticInstancer` for the whole module batches every decoration into one
 * draw call per unique (geometry, material) — a master GLB may have several sub-meshes,
 * so every renderable part gets its own instance handle, composed onto the row's world
 * transform (see `collectMeshParts`). Follows the `CommonsSettlementsModule` pattern.
 */
import type { App, IModule } from 'enginex';
import * as THREE from 'three/webgpu';
import type { DbConnection } from '../module_bindings/index.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { groundY } from '../lib/grounding.js';
import { structureSeats } from '../lib/structure_seat.js';
import { StaticInstancer, collectMeshParts } from '../lib/instancing.js';
import { furnitureFit, furnitureSpecOf, localToWorld, canAdminStructureUi } from '../lib/furniture_place.js';
import { Phase, makeSystem } from '../engine-compat.js';
import { sfx } from '../lib/sfx.js';
import type { AssetModule } from './AssetModule.js';

/** One placed decoration: every StaticInstancer handle it contributed (multi-submesh
 *  masters contribute one handle per part) + the asset ref held for its lifetime. */
interface PlacedItem { handles: number[]; assetId: bigint; release: () => void; parentY: number; }

export class DecorationModule implements IModule {
  readonly name = 'Decoration';
  private scene!: THREE.Scene;
  private instancer: StaticInstancer | null = null;
  private readonly placed = new Map<string, PlacedItem>(); // itemId → placed
  private syncDirty = false; // a sync request arrived while one was in flight
  private subscribed = false;
  private syncing = false;

  constructor(private readonly assets: AssetModule) {}

  private prompt: HTMLDivElement | null = null;

  private onKey: ((e: KeyboardEvent) => void) | null = null;
  private nearItemId: bigint | null = null;
  private acc = 0;

  init(app: App): void {
    this.scene = app.world.scene;
    this.instancer = new StaticInstancer(this.scene, 4096, 'decorations');
    this.instancer.setRainLayer(true); // awnings and street furniture shelter (render wave 1)
    bus.on('atlas:account-ready', () => { this.subscribe(); void this.sync(); });
    for (const ev of ['atlas:structures-updated', 'atlas:world-entered'] as const) bus.on(ev, () => void this.sync());
    // Furnishing wave: pick a crafted piece back up (F at the furniture) —
    // prompt only inside a structure the player may furnish; server re-checks.
    const prompt = document.createElement('div');
    prompt.id = 'furniture-prompt';
    prompt.style.cssText = 'position:fixed;left:50%;bottom:262px;transform:translateX(-50%);z-index:41;display:none;'
      + 'padding:6px 14px;border-radius:var(--ax-radius);background:color-mix(in srgb, var(--ax-bg2) 85%, transparent);border:1px solid var(--ax-line2);'
      + 'font:600 13px var(--ax-font);color:var(--ax-text);pointer-events:none';
    document.body.appendChild(prompt);
    this.prompt = prompt;
    this.onKey = (e: KeyboardEvent): void => {
      if (e.code !== 'KeyF' || e.ctrlKey || e.metaKey) return;
      const t2 = e.target as HTMLElement | null;
      if (t2 && (t2.tagName === 'INPUT' || t2.tagName === 'TEXTAREA')) return;
      if (this.nearItemId === null) return;
      const id = this.nearItemId;
      atlas.conn?.reducers.pickupFurniture({ structureItemId: id })
        .then(() => { sfx('sfx_pickup', { gain: 0.4 }); bus.emit('atlas:status', { text: 'Picked up — it is back in your inventory (Craft panel).' }); })
        .catch((err: Error) => bus.emit('atlas:status', { text: err.message }));
    };
    window.addEventListener('keydown', this.onKey);
    app.world.addSystem(makeSystem('FurniturePickup', Phase.UI, 33, () => this.pickupTick()));
  }

  /** ~6 Hz proximity scan for the nearest crafted piece the player may lift. */
  private pickupTick(): void {
    if (++this.acc % 10 !== 0) return;
    const conn = atlas.conn;
    const prompt = this.prompt;
    if (!conn || !conn.identity || !prompt) return;
    let bestId: bigint | null = null;
    let bestName = '';
    let bestD = 3.2;
    for (const item of conn.db.structureItem.iter()) {
      if (item.itemKey === '') continue; // free decor is not liftable
      const s = conn.db.structure.structureId.find(item.structureId);
      if (!s || s.worldId !== atlas.currentWorldId) continue;
      if (Math.abs(s.posX - atlas.localX) > 60 || Math.abs(s.posZ - atlas.localZ) > 60) continue;
      const w = localToWorld(s, item.lx, item.lz);
      const d = Math.hypot(w.x - atlas.localX, w.z - atlas.localZ);
      if (d < bestD && canAdminStructureUi(conn, item.structureId, conn.identity)) {
        bestD = d;
        bestId = item.itemId;
        bestName = item.prefabId.startsWith('furn_') ? item.prefabId.slice(5).replace(/_/g, ' ') : item.prefabId;
      }
    }
    this.nearItemId = bestId;
    prompt.style.display = bestId !== null ? 'block' : 'none';
    if (bestId !== null) prompt.textContent = `F — pick up the ${bestName}`;
  }

  private subscribe(): void {
    if (this.subscribed) return;
    const conn = atlas.conn; if (!conn) return;
    this.subscribed = true;
    conn.subscriptionBuilder().onApplied(() => void this.sync())
      .subscribe(['SELECT * FROM structure_item', 'SELECT * FROM structure', 'SELECT * FROM prefab_model', 'SELECT * FROM render_asset', 'SELECT * FROM world', 'SELECT * FROM terrain_region']);
    const t = conn.db.structureItem as unknown as { onInsert(cb: () => void): void; onUpdate(cb: () => void): void; onDelete(cb: () => void): void };
    // Trailing debounce (the StructureModule law): the initial sync replays
    // every item row and each ping rebuilt three panels' innerHTML + the
    // shadow update per ROW — O(rows²) at join (2026-09-12).
    let timer: number | null = null;
    const ping = (): void => {
      if (timer !== null) return;
      timer = window.setTimeout(() => { timer = null; bus.emit('atlas:structures-updated', {}); }, 150);
    };
    t.onInsert(ping); t.onUpdate(ping); t.onDelete(ping);
  }

  /** prefabKey → assetId, built once per sync pass (it used to spread the whole prefab table PER ITEM). */
  private prefabIndex: Map<string, bigint> | null = null;
  private decorAsset(conn: DbConnection, prefabId: string): bigint {
    if (!this.prefabIndex) { this.prefabIndex = new Map(); for (const p of conn.db.prefabModel.iter()) this.prefabIndex.set(p.prefabKey, p.assetId); }
    return this.prefabIndex.get(`decor:${prefabId}`) ?? 0n;
  }

  /** World transform = grounded structure transform ∘ the item's local transform. */
  private worldMatrix(sy: number, item: { lx: number; ly: number; lz: number; pitch: number; yaw: number; roll: number }, structure: { posX: number; posZ: number; yaw: number }, scale = 1): THREE.Matrix4 {
    const sMat = new THREE.Matrix4().compose(
      new THREE.Vector3(structure.posX, sy, structure.posZ),
      new THREE.Quaternion().setFromEuler(new THREE.Euler(0, structure.yaw, 0)),
      new THREE.Vector3(1, 1, 1),
    );
    const lMat = new THREE.Matrix4().compose(
      new THREE.Vector3(item.lx, item.ly, item.lz),
      new THREE.Quaternion().setFromEuler(new THREE.Euler(item.pitch, item.yaw, item.roll)),
      new THREE.Vector3(scale, scale, scale),
    );
    return sMat.multiply(lMat);
  }

  private async sync(): Promise<void> {
    const conn = atlas.conn;
    if (!conn || !this.instancer) return;
    // Re-entrant sync while a pass is awaiting loads must RE-RUN, not drop —
    // dropped syncs left stale decor across world hops (the StructureRender
    // d46e33f bug, same class; sweep-confirmed here).
    if (this.syncing) { this.syncDirty = true; return; }
    this.syncing = true;
    this.prefabIndex = null; // rebuilt lazily on the first item of this pass
    const instancer = this.instancer;
    // Capture the world at the start of this pass; if the player world-hops
    // while we are awaiting asset loads the pass is now stale and must be
    // re-started for the new world (BUG FIX: world-transition race).
    const startWorld = atlas.currentWorldId;
    const instMat = new THREE.Matrix4();
    try {
      const live = new Set<string>();
      for (const item of conn.db.structureItem.iter()) {
        const structure = conn.db.structure.structureId.find(item.structureId);
        if (!structure) continue;
        if (structure.worldId !== atlas.currentWorldId) continue; // only the world the player is in (the structure render is gated the same way)
        const assetId = this.decorAsset(conn, item.prefabId);
        if (assetId === 0n) continue;
        const key = item.itemId.toString();
        live.add(key);
        // Parent seat: the EXACT footprint-aware Y StructureRenderModule seated
        // the structure at (single-point groundY disagrees on slopes — decor
        // sheared off its shelf). Absent → structure not placed yet; fall back
        // and re-place on the structures-updated ping its placement emits.
        const psy = structureSeats.get(item.structureId.toString())?.y
          ?? groundY(conn, structure.posX, structure.posZ);
        const existing = this.placed.get(key);
        if (existing && existing.assetId === assetId && existing.parentY === psy) continue;
        if (existing) this.remove(key);

        // Tier-matched load (potato → cooked LOD1 when present; see loadForTier).
        const { object: master, release } = await this.assets.loadForTier(assetId);
        // If the world changed while we were loading, abort this stale pass.
        // The finally block resets this.syncing so the re-invoke below can run.
        if (atlas.currentWorldId !== startWorld) {
          release();
          return;
        }
        if (this.placed.has(key)) { release(); continue; }

        // Instance matrix per part = row world transform ∘ the part's local-to-master
        // transform (master is identity-rooted, so matrixWorld IS that local transform).
        // Furnishing wave: generated furniture GLBs arrive at arbitrary scale and
        // pivot — fit them into the catalog dims and seat the measured base at ly.
        let fitItem = item;
        let fitScale = 1;
        const fspec = furnitureSpecOf(item.prefabId);
        if (fspec) {
          const bb = new THREE.Box3().setFromObject(master);
          const { scale, yOff } = furnitureFit(fspec, bb);
          fitScale = scale;
          fitItem = { ...item, ly: item.ly + yOff };
        }
        const placeMat = this.worldMatrix(psy, fitItem, structure, fitScale);
        const handles: number[] = [];
        for (const part of collectMeshParts(master)) {
          const id = instancer.add(part.geometry, part.material, instMat.multiplyMatrices(placeMat, part.local));
          if (id >= 0) handles.push(id);
        }
        this.placed.set(key, { handles, assetId, release, parentY: psy });
      }
      for (const key of [...this.placed.keys()]) if (!live.has(key)) this.remove(key);
    } finally {
      this.syncing = false;
      // If the world changed during this pass, the early-return path above left
      // placed items from the old world; kick off a fresh sync for the new world
      // so stale decorations are cleaned up and the new world's items appear.
      // A queued re-entrant request (syncDirty) re-runs for the same reason.
      if (atlas.currentWorldId !== startWorld || this.syncDirty) { this.syncDirty = false; void this.sync(); }
    }
  }

  private remove(key: string): void {
    const p = this.placed.get(key);
    if (!p) return;
    for (const id of p.handles) this.instancer?.remove(id);
    p.release(); // release AFTER instances are gone (matched to the tier-picked cache key)
    this.placed.delete(key);
  }

  dispose(): void {
    for (const key of [...this.placed.keys()]) this.remove(key);
    this.instancer?.dispose();
    if (this.onKey) window.removeEventListener('keydown', this.onKey);
    this.onKey = null;
    this.prompt?.remove();
    this.prompt = null;
  }

  get count(): number { return this.placed.size; }
}
